import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, Sparkle, Warning, X, ArrowRight } from "@phosphor-icons/react";
import { loadAnnouncements } from "../lib/announcementsStore";

const KINDS = {
  info: { icon: Bell, accent: "#9DB8FF", label: "Notice" },
  new: { icon: Sparkle, accent: "#FF8090", label: "New" },
  warning: { icon: Warning, accent: "#F5B544", label: "Heads up" },
};
export const ANNOUNCEMENT_KINDS = Object.fromEntries(Object.entries(KINDS).map(([k, v]) => [k, v.label]));

const STORE = "aa_closed_announcements";
const read = () => {
  try { return JSON.parse(localStorage.getItem(STORE) || "{}") || {}; } catch { return {}; }
};
const write = (v) => {
  try { localStorage.setItem(STORE, JSON.stringify(v)); } catch { /* private mode: it just comes back next time */ }
};

/** One small notice: a kind icon, optional title, the message, an optional link, and an X. */
export function AnnouncementCard({ a, onClose, preview = false }) {
  const kind = KINDS[a.kind] || KINDS.info;
  const Icon = kind.icon;
  const internal = a.link_url && a.link_url.startsWith("/");
  const linkProps = { className: "inline-flex items-center gap-1 mt-2 text-[11px] font-display font-bold hover:underline", style: { color: kind.accent } };
  const link = a.link_url
    ? internal
      ? <Link to={a.link_url} onClick={preview ? (e) => e.preventDefault() : onClose} {...linkProps}>{a.link_label || "Learn more"} <ArrowRight size={11} weight="bold" /></Link>
      : <a href={a.link_url} target="_blank" rel="noopener noreferrer" onClick={preview ? (e) => e.preventDefault() : undefined} {...linkProps}>{a.link_label || "Learn more"} <ArrowRight size={11} weight="bold" /></a>
    : null;

  return (
    <div
      className="relative w-full rounded-xl border border-white/10 bg-[#161616]/[0.98] shadow-[0_18px_50px_-18px_rgba(0,0,0,0.9)] overflow-hidden"
      role="status"
      data-testid={preview ? "announcement-preview" : `announcement-${a.id}`}
    >
      <span className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: kind.accent }} aria-hidden="true" />
      <div className="flex gap-3 pl-4 pr-10 py-3.5">
        <span className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${kind.accent}22`, color: kind.accent }}>
          <Icon size={14} weight="fill" />
        </span>
        <div className="min-w-0">
          {a.title && <div className="font-display font-bold text-[13px] leading-snug mb-0.5 break-words">{a.title}</div>}
          <p className="text-[12.5px] leading-relaxed text-white/70 whitespace-pre-line break-words">{a.message || "Your message appears here."}</p>
          {link}
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close announcement"
        data-testid={preview ? "announcement-preview-close" : `announcement-close-${a.id}`}
        className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center text-white/45 hover:text-white hover:bg-white/10 transition"
      >
        <X size={13} weight="bold" />
      </button>
    </div>
  );
}

/**
 * The live announcements, fading up in the top-right corner (full width under the bar on
 * phones). They show a moment after the site opens, and each one can be closed — it stays
 * closed on that device until the admin edits it.
 */
export default function AnnouncementToasts() {
  const [items, setItems] = useState([]);
  const [closed, setClosed] = useState(read);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let live = true;
    loadAnnouncements().then((list) => { if (live) setItems(list); });
    const t = setTimeout(() => setReady(true), 1200); // a beat after the page appears, so it feels deliberate
    return () => { live = false; clearTimeout(t); };
  }, []);

  const close = useCallback((a) => {
    setClosed((prev) => {
      const next = { ...prev, [a.id]: a.version };
      write(next);
      return next;
    });
  }, []);

  const visible = ready ? items.filter((a) => closed[a.id] !== a.version) : [];

  return (
    <div
      className="fixed z-[45] top-[88px] left-3 right-3 sm:left-auto sm:right-5 sm:w-[340px] flex flex-col gap-2 pointer-events-none"
      data-testid="announcements"
    >
      <AnimatePresence initial={false}>
        {visible.map((a, i) => (
          <motion.div
            key={a.id + a.version}
            layout
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.18, ease: [0.22, 1, 0.36, 1] } }}
            exit={{ opacity: 0, y: -6, scale: 0.97, transition: { duration: 0.22 } }}
            className="pointer-events-auto"
          >
            <AnnouncementCard a={a} onClose={() => close(a)} />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
